import {
  add,
  addDays,
  eachDayOfInterval,
  formatDistanceStrict,
  isBefore,
  Locale,
  startOfDay,
  subDays,
  // eslint-disable-next-line no-restricted-imports
  format,
  differenceInHours,
  isValid,
  startOfToday,
  differenceInDays,
} from 'date-fns';
import { FULL_DAY_HOURS } from 'src/listing/constants';
import {
  BOOK_NOW_DATE_FORMAT,
  DISPLAY_DATE_FORMAT, DISPLAY_DATE_FORMAT_SHORT,
  TIME_12, TIME_24,
  COOKIE_POLICY_DATE_FORMAT,
} from '../constants';
import captureException from './captureException';

/**
 * This should only be used with dates that are constructed with a date string
 */
const adjustForTimezones = (date: Date): Date => new Date(
  date.getTime() + (date.getTimezoneOffset() * 60000),
);

/**
 * Represent a date in 'yyyy-MM-dd' format e.g. `2021-01-08`.
 */
export type DateString = string; // no type checking;

export const BOOK_NOW_PICK_UP_TIME_FORMAT = 'HH:mm:ss';

export type TimeFormat =
  | typeof BOOK_NOW_PICK_UP_TIME_FORMAT /** Time with seconds: 01:59:01 */
  | typeof TIME_24 /** 24 hour time */
  | typeof TIME_12; /** 12 hour time */

/** Date formats that do not require localization */
type DateFormatsNumeric =
  | 'dd'
  | 'MM/dd/yyyy'
  | TimeFormat
  | 'yyyy-MM-dd HH:mm:ss'
  | 'yyyy-MM-dd' /** `DATE_FORMAT` ISO date / Calendar date  */
  | 'yyyy' /** Year */
  | "yyyy-MM-dd'T'HH:mm" /** `OFFER_EXPIRY_DATE_FORMAT` T is quoted for escaping the character */
  ;

/** Date formats that require localization  */
type DateFormatsRequireLocalization =
  | typeof DISPLAY_DATE_FORMAT_SHORT /** dd MMM, e.g., 01 Jan */
  | 'dd MMM yyyy hh:mm a' /** e.g., 01 Jan 2024 01:15 PM */
  | typeof COOKIE_POLICY_DATE_FORMAT
  | typeof DISPLAY_DATE_FORMAT /** dd MMM yyyy, e.g.,: 01 Jan 2024 */
  | typeof BOOK_NOW_DATE_FORMAT /** E d MMM yyyy, e.g., Mon 1 Jan 2024 */
  | 'EEE d' /** Trip Cart start date, e.g., Mon 1 - ... */
  | 'EEE d MMM' /** Instabook Date, e.g., Mon 1 Jan */
  | 'E d MMM' /** Mon 1 Jan */
  | 'EEE MMM dd, yyyy' /** e.g., Mon Jan 01, 2024 */
  | 'EEE, MMM dd, yyyy' /** e.g., Mon, Jan 01, 2024 */
  | 'iii dd' /** e.g., Mon 01 */
  | 'iii dd MMM' /** e.g Mon 01 Jan */
  | 'iii dd MMM yyyy' /** e.g Mon 01 Jan 2024 */
  | 'LLL d' /** Month short day of month, e.g., Jun 16 */
  | 'MMM d' /** `DATE_FORMAT_SHORT: `MMM d`, e.g., Jan 1` */
  | 'MMM dd' /** Short month and date, e.g., Jan 02 */
  | 'MMM do' /** Short month and date (Ordinal), e.g., June 16th */
  | 'MMM' /** Short month name, e.g., Jan */
  | 'MMM yyyy' /** e.g., Jan 2024 */
  | 'MMMM d' /** e.g., January 1 */
  | 'MMMM dd, yyyy' /** Journal Entry Date, e.g., January 01, 2024 */
  | 'MMMM dd' /** Full month and date:, e.g., January 02 */
  | 'MMMM do' /** Full month and date (Ordinal), e.g., January 1st */
  | 'MMMM, yyyy' /** Full month and year, Review Date, e.g., January, 2024 */
  | 'MMMM yyyy' /** Full month and year, Review Date, e.g., January 2024 */
  | 'MMMM' /** Full month name, e.g., January */
  | 'p' /** Long localized time, e.g., 12:00 AM */
  | 'P' /** Long localized date, e.g., 04/29/1453 */
  ;

/** Supported date formats */
export type DateFnsFormats = DateFormatsNumeric | DateFormatsRequireLocalization;

interface FormatDate {
  /**
   * Formats a date object using `date-fns`.
   *
   * @param date The date to format
   * @param formatString The `date-fns` format string to use.
   *   Defaults to `yyyy-MM-dd`.
   *   e.g. `2024-09-03`
   * @param locale Required for displaying non numeric date appropriately for the locale.
   *
   * @example
   * ```ts
   * // Default format is `yyyy-MM-dd` and does not require localization.
   * formatDate(selectedDate);
   *
   * // Numeric format does not require localization
   * formatDate(selectedDate, 'yyyy-MM-dd');
   * formatDate(selectedDate, 'yyyy');
   *
   * // Non-numeric formats do require localization
   * const dateFnsLocale = useDateFnsLocale();
   * formatDate(selectedDate, 'MMM do', dateFnsLocale);
   * formatDate(selectedDate, 'h:mm a', dateFnsLocale);
   * formatDate(selectedDate, 'EEE MMM dd, yyyy', dateFnsLocale);
   * ```
   *
   * ### Non-numeric formats do require localization
   */
  (
    date: Date,
    formatString: DateFormatsRequireLocalization,
    locale: Locale
  ): string;
  (
    date: Date,
    formatString: DateFormatsNumeric,
    locale?: Locale
  ): string;
  (date: Date): string;
}

export const formatDate: FormatDate = (
  date: Date,
  formatString: DateFnsFormats = 'yyyy-MM-dd',
  locale?: Locale,
): string => format(date, formatString, { locale });

/**
 * Returns a date that has been adjusted for timezones.
 */
const newTZDate = (date: string): Date => adjustForTimezones(new Date(date));

/**
 * The common display date format 'dd MMM yyyy' or 'dd MMM'
 */
export type DisplayDateString = string;

/**
 * Formats a date string to a display-friendly format.
 * @param {DateString} date - The input date string to format.
 * @param {boolean} displayYear - Whether to include the year in the formatted date.
 * @returns The formatted date in 'dd MMM yyyy' or 'dd MMM' format e.g. '15 Sep 2023' or '15 Sep'.
 */
export const formatDateForDisplay = (
  date: string,
  // eslint-disable-next-line @typescript-eslint/default-param-last
  displayYear: boolean = true,
  locale: Locale,
): DisplayDateString => (date ? formatDate(
  newTZDate(date),
  displayYear ? DISPLAY_DATE_FORMAT : DISPLAY_DATE_FORMAT_SHORT,
  locale,
) : '');

/**
 * Returns a Date representing the start of today.
 */
export const todayDate = (): Date => startOfDay(new Date());

/**
 * Returns an array of dates for date filters.
 * Dates that are in the past are filtered out.
 * Dates are ordered with the base date first, followed by the additional dates
 * in ascending chronological order. Index zero is treated as the primary date
 * with the others being secondary.
 *
 * @example
 * ```ts
 * getDatesInRange(newTZDate('2029-02-23'));
 * // [Date(2029-02-23)]
 * getDatesInRange(newTZDate('2029-02-23'), 0);
 * // [Date(2029-02-23)]
 * getDatesInRange(newTZDate('2029-02-23'), 1);
 * // [
 * //   Date(2029-02-23), The input date
 * //   Date(2029-02-22),
 * //   Date(2029-02-24),
 * // ]
 * getDatesInRange(newTZDate('2029-02-23'), 2);
 * // [
 * //   Date(2029-02-23), The input date
 * //   Date(2029-02-21),
 * //   Date(2029-02-22),
 * //   Date(2029-02-24),
 * //   Date(2029-02-25),
 * // ]
 * ```
 */
export const getDatesInRange = (givenDate: Date | null, rangeOption = 0): Date[] => {
  if (rangeOption < 0) {
    return [];
  }
  const selectedDate = givenDate ?? todayDate();
  const datesInRange = eachDayOfInterval({
    start: subDays(selectedDate, rangeOption),
    end: addDays(selectedDate, rangeOption),
  });
  datesInRange.splice(rangeOption, 1);
  datesInRange.unshift(selectedDate);
  return datesInRange.filter((dateInRange) => !isBefore(dateInRange, todayDate()));
};

export const getNextDay = (
  date: string,
): DisplayDateString | null => {
  if (!date) return null;
  const nextDay = addDays(new Date(date), 1);
  return format(nextDay, DISPLAY_DATE_FORMAT);
};

/**
 *
 * Returns an object representing the duration of a timespan.
 */
export const convertTimeSpanToDuration = (value: string): Duration => {
  const regex = /(\d+|)(?:\.|\s|)(\d\d:\d\d:\d\d)/;

  try {
    if (!regex.test(value)) throw new Error(`Invalid timespan: ${value}`);
  } catch (err) {
    captureException(err);
  }
  const [, days, time] = regex.exec(value) as string[];
  const [hours, minutes] = time.split(':');
  return {
    days: parseInt(days || '0', 10),
    hours: parseInt(hours, 10),
    minutes: parseInt(minutes, 10),
  };
};

export default newTZDate;

export const roundToNextMinutes = (
  date: Date = new Date(),
  { roundToNext = 15 }: { roundToNext?: number } = {},
): Date => {
  const minutes = date.getMinutes();
  const roundedMinutes = Math.ceil(minutes / roundToNext) * roundToNext;

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    roundedMinutes,
  );
};

export const adjustTimezone = (date: number | Date, offsetInHours: number): Date => {
  const hours = offsetInHours > 0 ? Math.floor(offsetInHours) : Math.ceil(offsetInHours);
  const minutes = (offsetInHours - hours) * 60;
  return add(date, { hours, minutes });
};

type Time = { formattedTime: string; days: number };

export const normalizeTime = (value: string = ''): Time => {
  const [originalHours = 0, originalMinutes = 0] = value.split(':');
  const roundedMinutes = (Math.round(Number(originalMinutes) / 15) * 15);
  const roundedHours = Math.floor(roundedMinutes / 60) + Number(originalHours);
  const days = Math.floor(roundedHours / FULL_DAY_HOURS);
  const hours = `${roundedHours % FULL_DAY_HOURS}`;
  const minutes = `${roundedMinutes % 60}`;
  return {
    formattedTime: `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`,
    days,
  };
};

// eslint-disable-next-line @typescript-eslint/default-param-last
export const convertToTimeZone = (date: Date | string, timeZone: string): Date => new Date(
  (typeof date === 'string' ? new Date(date) : date).toLocaleString('en-US', { timeZone }),
);

export const calculateTimezoneOffsetInHours = (a?: string, b?: string): number => {
  if (!a || !b) return 0;
  const date = new Date();
  const diff = convertToTimeZone(date, a).getTime() - convertToTimeZone(date, b).getTime();
  return diff / 3600000; // Milliseconds in an hour
};

export const calculateExpiryTimeRemaining = (
  expiryDate: Date,
  date: Date,
  timezoneOffsetInHours: number = 0,
): string => {
  const updatedExpiryDate = add(expiryDate, { hours: timezoneOffsetInHours });
  return formatDistanceStrict(date, updatedExpiryDate);
};

type FormatDateRangeOptions = {
  includeDay?: boolean;
  excludeYear?: boolean;
};
export const formatDateRange = (
  startDate: Date,
  endDate: Date | null,
  locale: Locale,
  options?: FormatDateRangeOptions,
): string | null => {
  if (!startDate) return null;
  const displayYear: DateFormatsRequireLocalization = options?.includeDay
    ? 'iii dd MMM yyyy'
    : 'dd MMM yyyy';
  const displayMonth: DateFormatsRequireLocalization = options?.includeDay
    ? 'iii dd MMM'
    : 'dd MMM';
  const displayDay: DateFormatsRequireLocalization | DateFormatsNumeric = options?.includeDay
    ? 'iii dd'
    : 'dd';

  const isSameDate = startDate && endDate
    && differenceInHours(startOfDay(endDate), startOfDay(startDate)) < FULL_DAY_HOURS;
  if (!endDate || isSameDate) {
    return formatDate(startDate, options?.excludeYear ? displayMonth : displayYear, locale);
  }

  let start;
  // same year and month, e.g. 04 - 20 Jan 2025
  if (format(startDate, 'yyyy-MM') === format(endDate, 'yyyy-MM')) {
    start = formatDate(startDate, displayDay as DateFormatsNumeric, locale);
  // same year, different month, e.g. 04 Jan - 20 Mar 2025
  } else if (startDate.getFullYear() === endDate.getFullYear()) {
    start = formatDate(startDate, displayMonth, locale);
  // different year, e.g. 04 Dec 2025 - 20 Jan 2026
  } else {
    start = formatDate(startDate, options?.excludeYear ? displayMonth : displayYear, locale);
  }

  const end = endDate && (
    formatDate(endDate, options?.excludeYear ? displayMonth : displayYear, locale)
  );
  return `${start} - ${end}`;
};

export const isDateValid = (dateStr: string): boolean => isValid(new Date(dateStr));

export const isDateInFutureForTimeZone = (
  date: string,
  listingTimeZone?: string,
): boolean => {
  // If the date or timezone is missing or invalid, treat it as valid.
  // This prevents blocking form submissions in cases like the inquiry form.
  if (!isDateValid(date) || !listingTimeZone) return true;

  const selectedDate = new Date(date);
  const currentDate = convertToTimeZone(new Date(), listingTimeZone);

  return isBefore(currentDate, selectedDate);
};

export const getDaysUntilInstabook = (dates: Date | Date[], instabookDates: string[]): number => {
  if (
    !dates
    || (Array.isArray(dates) && !dates.length)
     || !instabookDates
     || !instabookDates.length) return 0;

  const selectedDate = Array.isArray(dates)
    ? dates[0]
    : dates;

  if (!instabookDates.includes(formatDate(selectedDate))) return 0;
  return differenceInDays(selectedDate, startOfToday());
};
